/**
 * ads.ts — the Ads research feature (LinkedIn first): what we watch, what competitors run, and how our own ads perform.
 *
 * Rules that come from the team's playbook and are enforced here, in code, not in a prompt:
 *   - Unknown is "Not available", never 0. Every metric that can be unknown is `null`, and totals say how many rows were missing.
 *   - Competitor ads are "observed" by a person (typed in or imported); nothing here is scraped.
 *   - Importance (1–5) is a fixed, explainable rule (`scoreImportance`) and every point is listed.
 *   - Nothing is deleted: rows are retired.
 */
import { z } from "zod";
import { parseCsv } from "./social.ts";
import { AD_CHANNELS, AD_CHANNEL_LABEL, AD_PRODUCTS, adChannelSchema, adProductIdSchema, type AdChannel, type AdProductId } from "./adsProducts.ts";
import { parseAdCode } from "./adsLinks.ts";

export const ADS_LIMITS = {
  csvMaxRows: 500,
  csvMaxChars: 500_000,
  csvMaxErrors: 40,
  textMax: 5000,
  /** The team's rule: a company or term with nothing relevant for this many weeks in a row is retired. */
  retireAfterEmptyWeeks: 3,
} as const;

// --------------------------------------------------------------------------------------------------------------------
// Small helpers
// --------------------------------------------------------------------------------------------------------------------

export function isValidIsoDate(s: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return false;
  const [y, m, d] = s.split("-").map(Number) as [number, number, number];
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.getUTCFullYear() === y && dt.getUTCMonth() === m - 1 && dt.getUTCDate() === d;
}
export const isoDateSchema = z.string().refine(isValidIsoDate, "Use a real date written YYYY-MM-DD");

const dayNumber = (s: string) => {
  const [y, m, d] = s.split("-").map(Number) as [number, number, number];
  return Date.UTC(y, m - 1, d) / 86_400_000;
};
const fromDayNumber = (n: number) => new Date(n * 86_400_000).toISOString().slice(0, 10);

/** The Monday of the week a date falls in (weeks are Monday–Sunday). */
export function weekStartOf(date: string): string {
  const n = dayNumber(date);
  const dow = new Date(n * 86_400_000).getUTCDay(); // 0 = Sunday
  return fromDayNumber(n - ((dow + 6) % 7));
}

/** Days an ad ran, counting both the first and the last day. `null` when either date is unknown. */
export function daysRunning(firstSeen: string | null, lastSeen: string | null): number | null {
  if (!firstSeen || !lastSeen || !isValidIsoDate(firstSeen) || !isValidIsoDate(lastSeen)) return null;
  const n = dayNumber(lastSeen) - dayNumber(firstSeen) + 1;
  return n >= 1 ? n : null;
}

/** "https://www.Tebra.com/pricing" → "tebra.com". Returns null for anything that is not a plausible host name. */
export function normalizeDomain(input: string): string | null {
  let v = input.trim().toLowerCase();
  if (!v) return null;
  v = v.replace(/^[a-z][a-z0-9+.-]*:\/\//, "").replace(/^www\./, "");
  v = v.split(/[/?#]/)[0] ?? "";
  v = v.replace(/:\d+$/, "");
  return /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/.test(v) ? v : null;
}

/** The key that stops the same name being added twice ("  NexHealth " and "nexhealth" are one). */
export const nameKey = (name: string) => name.trim().replace(/\s+/g, " ").toLowerCase();

/** A copied ad that looks cut off ("…", "see more") — the playbook says to expand it before saving. */
export function looksTruncated(text: string): boolean {
  const t = text.trim();
  return /(\.\.\.|…)$/.test(t) || /\bsee more\b\s*$/i.test(t) || /\b…\s*more\s*$/i.test(t);
}

// --------------------------------------------------------------------------------------------------------------------
// Watchlist
// --------------------------------------------------------------------------------------------------------------------

export const WATCH_TYPES = ["company", "term"] as const;
export const watchTypeSchema = z.enum(WATCH_TYPES);
export const TERM_FAMILIES = ["product", "problem", "audience", "new"] as const;
export const termFamilySchema = z.enum(TERM_FAMILIES);
export const TERM_FAMILY_LABEL: Record<(typeof TERM_FAMILIES)[number], string> = {
  product: "Product (what we sell)",
  problem: "Problem (the pain)",
  audience: "Audience (who buys)",
  new: "New (emerging language, news, rules)",
};
export const WATCH_CATEGORIES = ["direct_competitor", "trendsetter"] as const;
export const watchCategorySchema = z.enum(WATCH_CATEGORIES);
export const WATCH_CATEGORY_LABEL: Record<(typeof WATCH_CATEGORIES)[number], string> = {
  direct_competitor: "Direct competitor",
  trendsetter: "Trendsetter",
};
/** Who put a row there: a person, an AI run, or the CMS setup itself. */
export const addedByTypeSchema = z.enum(["human", "ai", "setup"]);

const trimmed = (max: number) => z.string().trim().max(max);

export const watchlistInputSchema = z
  .object({
    siteId: z.string().min(1).max(100),
    /** Each product keeps its own list, so SonicHire's competitors never feed Raya's ads. */
    product: adProductIdSchema,
    type: watchTypeSchema,
    termFamily: termFamilySchema.nullable().default(null),
    category: watchCategorySchema.nullable().default(null),
    name: trimmed(120).min(1, "Give it a name"),
    domain: trimmed(200).nullable().default(null),
    pinned: z.boolean().default(false),
    reason: trimmed(500).default(""),
    sourceUrl: trimmed(500).nullable().default(null),
  })
  .superRefine((v, ctx) => {
    if (v.type === "company") {
      if (!v.category) ctx.addIssue({ code: "custom", path: ["category"], message: "Choose direct competitor or trendsetter" });
      if (v.termFamily) ctx.addIssue({ code: "custom", path: ["termFamily"], message: "Only search terms have a family" });
    } else {
      if (!v.termFamily) ctx.addIssue({ code: "custom", path: ["termFamily"], message: "Choose a term family" });
      if (v.category) ctx.addIssue({ code: "custom", path: ["category"], message: "Only companies are direct competitors or trendsetters" });
      if (v.domain) ctx.addIssue({ code: "custom", path: ["domain"], message: "A search term has no web address" });
    }
    if (v.domain && !normalizeDomain(v.domain)) ctx.addIssue({ code: "custom", path: ["domain"], message: "That is not a web address like tebra.com" });
    if (v.sourceUrl && !/^https?:\/\/\S+$/i.test(v.sourceUrl)) ctx.addIssue({ code: "custom", path: ["sourceUrl"], message: "A source must start with http:// or https://" });
  });
export type WatchlistInput = z.infer<typeof watchlistInputSchema>;

export const watchlistPatchSchema = z.object({
  siteId: z.string().min(1).max(100),
  pinned: z.boolean().optional(),
  reason: trimmed(500).optional(),
  category: watchCategorySchema.optional(),
  domain: trimmed(200).nullable().optional(),
  sourceUrl: trimmed(500).nullable().optional(),
});
export const retireBodySchema = z.object({ siteId: z.string().min(1).max(100), reason: trimmed(300).default("") });
export const siteBodySchema = z.object({ siteId: z.string().min(1).max(100) });

/** The handoff's rule: any unpinned company or term with no ads for 3 weeks in a row is switched off (never deleted). Pinned items are always checked. */
export function shouldAutoRetire(i: { emptyWeeks: number; pinned: boolean }): boolean {
  return !i.pinned && i.emptyWeeks >= ADS_LIMITS.retireAfterEmptyWeeks;
}

/**
 * Watchlist upkeep after checking one item. At most one empty week is counted per calendar week (`weekOf` is that week's Monday),
 * so pressing Fetch twice in a week cannot retire a company. Finding any ad resets the count.
 */
export function afterWatchCheck(
  current: { emptyWeeks: number; lastCheckedWeek: string | null; pinned: boolean },
  weekOf: string,
  adsFound: number
): { emptyWeeks: number; lastCheckedWeek: string; retire: boolean } {
  if (adsFound > 0) return { emptyWeeks: 0, lastCheckedWeek: weekOf, retire: false };
  const emptyWeeks = current.pinned || current.lastCheckedWeek === weekOf ? current.emptyWeeks : current.emptyWeeks + 1;
  return { emptyWeeks, lastCheckedWeek: weekOf, retire: shouldAutoRetire({ emptyWeeks, pinned: current.pinned }) };
}

// --------------------------------------------------------------------------------------------------------------------
// Importance (1–5)
// --------------------------------------------------------------------------------------------------------------------

export interface ImportanceInput {
  category: (typeof WATCH_CATEGORIES)[number] | null;
  /** Pinned means "always checked". A pinned direct competitor is the playbook's "pinned main competitor". */
  pinned: boolean;
  daysRunning: number | null;
  /** The lower end of the impressions band (the only end we can be sure of). */
  impressionsLow: number | null;
  /** Share of the audience in our markets, 0–100, as read from the ad library. */
  targetSharePct: number | null;
  isMarketing: boolean;
}
export interface ImportancePart {
  label: string;
  delta: number;
}
export interface Importance {
  score: 1 | 2 | 3 | 4 | 5;
  parts: ImportancePart[];
}

/**
 * The playbook's rule, exactly: start at 3; +1 direct competitor (+2 if it is the pinned main competitor); +1 if it ran
 * 90+ days or has 100k+ impressions; −2 if under 50% of its audience is in our markets; −1 if it is not really
 * marketing (a job ad, say); clamp to 1–5. A value we do not have never adds or removes a point.
 */
export function scoreImportance(i: ImportanceInput): Importance {
  const parts: ImportancePart[] = [{ label: "Starting point", delta: 3 }];
  if (i.category === "direct_competitor") {
    parts.push(i.pinned ? { label: "Pinned main competitor", delta: 2 } : { label: "Direct competitor", delta: 1 });
  }
  const longRun = i.daysRunning !== null && i.daysRunning >= 90;
  const big = i.impressionsLow !== null && i.impressionsLow >= 100_000;
  if (longRun || big) parts.push({ label: longRun && big ? "Ran 90+ days and 100k+ impressions" : longRun ? "Ran 90+ days" : "100k+ impressions", delta: 1 });
  if (i.targetSharePct !== null && i.targetSharePct < 50) parts.push({ label: "Under half of its audience is in our markets", delta: -2 });
  if (!i.isMarketing) parts.push({ label: "Not really marketing", delta: -1 });
  const raw = parts.reduce((a, p) => a + p.delta, 0);
  return { score: Math.min(5, Math.max(1, raw)) as Importance["score"], parts };
}

// --------------------------------------------------------------------------------------------------------------------
// Competitor ads (observed by a person)
// --------------------------------------------------------------------------------------------------------------------

export const AD_FORMATS = ["single_image", "video", "carousel", "document", "text", "event", "message", "thought_leader", "other"] as const;
export const adFormatSchema = z.enum(AD_FORMATS);
export const AD_FORMAT_LABEL: Record<(typeof AD_FORMATS)[number], string> = {
  single_image: "Single image",
  video: "Video",
  carousel: "Carousel",
  document: "Document",
  text: "Text only",
  event: "Event",
  message: "Message ad",
  thought_leader: "Thought leader ad",
  other: "Other",
};

const countryCode = z.string().trim().toUpperCase().regex(/^[A-Z]{2}$/, "Use two-letter country codes like US");
const count = z.number().int().min(0).max(1_000_000_000_000);

const competitorAdFields = z.object({
  siteId: z.string().min(1).max(100),
  headline: trimmed(300).default(""),
  fullText: trimmed(ADS_LIMITS.textMax).min(1, "Paste the full ad text"),
  cta: trimmed(80).nullable().default(null),
  format: adFormatSchema.default("single_image"),
  adUrl: trimmed(1000).nullable().default(null),
  landingUrl: trimmed(1000).nullable().default(null),
  firstSeen: isoDateSchema.nullable().default(null),
  lastSeen: isoDateSchema.nullable().default(null),
  impressionsLow: count.nullable().default(null),
  impressionsHigh: count.nullable().default(null),
  topCountries: z.array(countryCode).max(10).default([]),
  targetSharePct: z.number().min(0).max(100).nullable().default(null),
  isMarketing: z.boolean().default(true),
  imageText: trimmed(1000).default(""),
  why: trimmed(500).default(""),
  /** Left out = unchanged on an edit, so an imported analyst note is never wiped by a form that does not show it. */
  notes: trimmed(8000).optional(),
  detailsCheckedOn: isoDateSchema.nullable().default(null),
});
function checkCompetitorAd(v: z.infer<typeof competitorAdFields>, ctx: z.RefinementCtx): void {
  if (v.firstSeen && v.lastSeen && v.lastSeen < v.firstSeen) ctx.addIssue({ code: "custom", path: ["lastSeen"], message: "Last seen cannot be before first seen" });
  if (v.impressionsLow !== null && v.impressionsHigh !== null && v.impressionsHigh < v.impressionsLow) ctx.addIssue({ code: "custom", path: ["impressionsHigh"], message: "The top of the band cannot be below the bottom" });
  for (const k of ["adUrl", "landingUrl"] as const) {
    const val = v[k];
    if (val && !/^https?:\/\/\S+$/i.test(val)) ctx.addIssue({ code: "custom", path: [k], message: "A link must start with http:// or https://" });
  }
}
export const competitorAdInputSchema = competitorAdFields.extend({ watchlistId: z.string().min(1).max(100) }).superRefine(checkCompetitorAd);
/** The same checks without the competitor, for CSV rows (the competitor is looked up by name). */
const competitorAdRowSchema = competitorAdFields.superRefine(checkCompetitorAd);
export type CompetitorAdInput = z.infer<typeof competitorAdInputSchema>;

/** What makes two competitor ads "the same one": the same link when there is one, otherwise the same opening words. */
export function competitorAdKey(watchlistId: string, adUrl: string | null, fullText: string): string {
  const basis = adUrl ? adUrl.trim().toLowerCase().replace(/\/+$/, "") : fullText.toLowerCase().replace(/\s+/g, " ").trim().slice(0, 200);
  return `${watchlistId}|${basis}`;
}

export const competitorAdSchema = z.object({
  id: z.string(),
  watchlistId: z.string(),
  competitorName: z.string(),
  category: watchCategorySchema.nullable(),
  headline: z.string(),
  fullText: z.string(),
  cta: z.string().nullable(),
  format: adFormatSchema,
  adUrl: z.string().nullable(),
  landingUrl: z.string().nullable(),
  firstSeen: z.string().nullable(),
  lastSeen: z.string().nullable(),
  daysRunning: z.number().int().nullable(),
  impressionsLow: z.number().nullable(),
  impressionsHigh: z.number().nullable(),
  topCountries: z.array(z.string()),
  targetSharePct: z.number().nullable(),
  isMarketing: z.boolean(),
  imageText: z.string(),
  why: z.string(),
  notes: z.string(),
  detailsCheckedOn: z.string().nullable(),
  importance: z.object({ score: z.number().int().min(1).max(5), parts: z.array(z.object({ label: z.string(), delta: z.number() })) }),
  truncated: z.boolean(),
  addedByType: addedByTypeSchema,
  addedByName: z.string().nullable(),
  createdAt: z.string(),
  retiredAt: z.string().nullable(),
});
export type CompetitorAd = z.infer<typeof competitorAdSchema>;

export const watchlistItemSchema = z.object({
  id: z.string(),
  product: adProductIdSchema,
  type: watchTypeSchema,
  termFamily: termFamilySchema.nullable(),
  category: watchCategorySchema.nullable(),
  name: z.string(),
  domain: z.string().nullable(),
  pinned: z.boolean(),
  reason: z.string(),
  sourceUrl: z.string().nullable(),
  emptyWeeks: z.number().int(),
  addedByType: addedByTypeSchema,
  addedByName: z.string().nullable(),
  createdAt: z.string(),
  retiredAt: z.string().nullable(),
  retiredReason: z.string().nullable(),
  adCount: z.number().int(),
  lastAdSeen: z.string().nullable(),
  topImportance: z.number().int().min(1).max(5).nullable(),
});
export type WatchlistItem = z.infer<typeof watchlistItemSchema>;

// --------------------------------------------------------------------------------------------------------------------
// Own ad results
// --------------------------------------------------------------------------------------------------------------------

export const RESULT_SOURCES = ["manual", "csv", "sample", "linkedin_api", "google_ads"] as const;
const currencyCode = z.string().trim().toUpperCase().regex(/^[A-Z]{3}$/, "Use a three-letter currency code like USD");

export const adResultInputSchema = z
  .object({
    siteId: z.string().min(1).max(100),
    product: adProductIdSchema,
    /** Where the ad ran. Rows entered before channels were recorded are LinkedIn. */
    channel: adChannelSchema.default("linkedin"),
    periodStart: isoDateSchema,
    periodEnd: isoDateSchema,
    campaign: trimmed(120).min(1, "Give the campaign a name"),
    adName: trimmed(160).nullable().default(null),
    impressions: count.nullable().default(null),
    clicks: count.nullable().default(null),
    websiteClicks: count.nullable().default(null),
    conversions: count.nullable().default(null),
    spend: z.number().min(0).max(1_000_000_000).nullable().default(null),
    currency: currencyCode.nullable().default(null),
  })
  .superRefine((v, ctx) => {
    if (v.periodEnd < v.periodStart) ctx.addIssue({ code: "custom", path: ["periodEnd"], message: "The end cannot be before the start" });
    if (v.impressions !== null && v.clicks !== null && v.clicks > v.impressions) ctx.addIssue({ code: "custom", path: ["clicks"], message: "Clicks cannot be more than impressions" });
    if (v.spend !== null && !v.currency) ctx.addIssue({ code: "custom", path: ["currency"], message: "Say which currency the spend is in" });
  });
export type AdResultInput = z.infer<typeof adResultInputSchema>;

/** The same campaign/ad for the same period is one row, so importing a file twice cannot double the totals. */
export function adResultKey(r: { product?: string | null; channel?: string | null; periodStart: string; periodEnd: string; campaign: string; adName: string | null }): string {
  const base = [r.product ?? "", r.periodStart, r.periodEnd, r.campaign.trim().toLowerCase(), (r.adName ?? "").trim().toLowerCase()].join("|");
  // LinkedIn keys keep their old shape, so rows saved before channels existed still count as duplicates.
  return r.channel && r.channel !== "linkedin" ? `${base}|${r.channel}` : base;
}

export const adResultSchema = z.object({
  id: z.string(),
  /** Null for rows entered before products were separated. */
  product: adProductIdSchema.nullable(),
  periodStart: z.string(),
  periodEnd: z.string(),
  campaign: z.string(),
  adName: z.string().nullable(),
  channel: adChannelSchema,
  /** The draft this row belongs to, when its ad code was given. */
  creativeId: z.string().nullable(),
  impressions: z.number().nullable(),
  clicks: z.number().nullable(),
  websiteClicks: z.number().nullable(),
  conversions: z.number().nullable(),
  spend: z.number().nullable(),
  currency: z.string().nullable(),
  source: z.enum(RESULT_SOURCES),
  addedByName: z.string().nullable(),
  createdAt: z.string(),
  retiredAt: z.string().nullable(),
});
export type AdResult = z.infer<typeof adResultSchema>;

// --------------------------------------------------------------------------------------------------------------------
// Funnel maths: impressions → clicks → website clicks → conversions. Unknown is never 0.
// --------------------------------------------------------------------------------------------------------------------

export interface FunnelRow {
  impressions: number | null;
  clicks: number | null;
  websiteClicks: number | null;
  conversions: number | null;
  spend: number | null;
  currency: string | null;
}
type Metric = "impressions" | "clicks" | "websiteClicks" | "conversions";

export const metricTotalSchema = z.object({ value: z.number().nullable(), missingRows: z.number().int() });
export const rateSchema = z.object({ value: z.number(), rowsUsed: z.number().int(), rowsTotal: z.number().int() }).nullable();
export const funnelSchema = z.object({
  rows: z.number().int(),
  impressions: metricTotalSchema,
  clicks: metricTotalSchema,
  websiteClicks: metricTotalSchema,
  conversions: metricTotalSchema,
  spendByCurrency: z.record(z.string(), z.number()),
  spendMissingRows: z.number().int(),
  /** clicks ÷ impressions */
  ctr: rateSchema,
  /** website clicks ÷ clicks */
  websiteClickRate: rateSchema,
  /** conversions ÷ website clicks */
  conversionRate: rateSchema,
});
export type Funnel = z.infer<typeof funnelSchema>;

function total(rows: FunnelRow[], m: Metric): { value: number | null; missingRows: number } {
  let sum = 0;
  let known = 0;
  for (const r of rows) {
    const v = r[m];
    if (v === null) continue;
    sum += v;
    known++;
  }
  return { value: known === 0 ? null : sum, missingRows: rows.length - known };
}

/** A ratio over only the rows where BOTH numbers are known, so a missing figure is never counted as 0. */
function ratio(rows: FunnelRow[], num: Metric, den: Metric) {
  let n = 0;
  let d = 0;
  let used = 0;
  for (const r of rows) {
    const a = r[num];
    const b = r[den];
    if (a === null || b === null) continue;
    n += a;
    d += b;
    used++;
  }
  if (used === 0 || d === 0) return null;
  return { value: n / d, rowsUsed: used, rowsTotal: rows.length };
}

export function funnelOf(rows: FunnelRow[]): Funnel {
  const spendByCurrency: Record<string, number> = {};
  let spendMissing = 0;
  for (const r of rows) {
    if (r.spend === null || !r.currency) {
      spendMissing++;
      continue;
    }
    spendByCurrency[r.currency] = Math.round(((spendByCurrency[r.currency] ?? 0) + r.spend) * 100) / 100;
  }
  return {
    rows: rows.length,
    impressions: total(rows, "impressions"),
    clicks: total(rows, "clicks"),
    websiteClicks: total(rows, "websiteClicks"),
    conversions: total(rows, "conversions"),
    spendByCurrency,
    spendMissingRows: spendMissing,
    ctr: ratio(rows, "clicks", "impressions"),
    websiteClickRate: ratio(rows, "websiteClicks", "clicks"),
    conversionRate: ratio(rows, "conversions", "websiteClicks"),
  };
}

export const adsSummarySchema = z.object({
  realRows: z.number().int(),
  overall: funnelSchema,
  weekly: z.array(z.object({ weekStart: z.string(), funnel: funnelSchema })),
  campaigns: z.array(z.object({ campaign: z.string(), funnel: funnelSchema })),
  /** Per channel, so LinkedIn and Google are never added together. */
  channels: z.array(z.object({ channel: adChannelSchema, funnel: funnelSchema })),
  /** Per ad (named rows only), with the channel it ran on. */
  ads: z.array(z.object({ adName: z.string(), channel: adChannelSchema, creativeId: z.string().nullable(), funnel: funnelSchema })),
});
export type AdsSummary = z.infer<typeof adsSummarySchema>;

/** Groups rows by the Monday of the week their period starts in (oldest first). */
export function weeklyFunnels(rows: Array<FunnelRow & { periodStart: string }>): Array<{ weekStart: string; funnel: Funnel }> {
  const by = new Map<string, FunnelRow[]>();
  for (const r of rows) {
    const k = weekStartOf(r.periodStart);
    by.set(k, [...(by.get(k) ?? []), r]);
  }
  return [...by.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([weekStart, rs]) => ({ weekStart, funnel: funnelOf(rs) }));
}

/** Per campaign, biggest impressions first (unknown impressions last). */
export function campaignFunnels(rows: Array<FunnelRow & { campaign: string }>): Array<{ campaign: string; funnel: Funnel }> {
  const by = new Map<string, FunnelRow[]>();
  for (const r of rows) by.set(r.campaign, [...(by.get(r.campaign) ?? []), r]);
  return [...by.entries()]
    .map(([campaign, rs]) => ({ campaign, funnel: funnelOf(rs) }))
    .sort((a, b) => (b.funnel.impressions.value ?? -1) - (a.funnel.impressions.value ?? -1) || a.campaign.localeCompare(b.campaign));
}

/** Per channel (LinkedIn first). */
export function channelFunnels(rows: Array<FunnelRow & { channel: AdChannel }>): Array<{ channel: AdChannel; funnel: Funnel }> {
  return AD_CHANNELS.flatMap((channel) => {
    const rs = rows.filter((r) => r.channel === channel);
    return rs.length ? [{ channel, funnel: funnelOf(rs) }] : [];
  });
}

/** Per named ad and channel, biggest impressions first. Rows with no ad name are left to the campaign view. */
export function adFunnels(rows: Array<FunnelRow & { adName: string | null; channel: AdChannel; creativeId: string | null }>): Array<{ adName: string; channel: AdChannel; creativeId: string | null; funnel: Funnel }> {
  const by = new Map<string, { adName: string; channel: AdChannel; creativeId: string | null; rows: FunnelRow[] }>();
  for (const r of rows) {
    const name = (r.adName ?? "").trim();
    if (!name) continue;
    const key = `${r.channel}|${name.toLowerCase()}`;
    const cur = by.get(key) ?? { adName: name, channel: r.channel, creativeId: r.creativeId, rows: [] };
    cur.rows.push(r);
    cur.creativeId = cur.creativeId ?? r.creativeId;
    by.set(key, cur);
  }
  return [...by.values()]
    .map(({ rows: rs, ...rest }) => ({ ...rest, funnel: funnelOf(rs) }))
    .sort((a, b) => (b.funnel.impressions.value ?? -1) - (a.funnel.impressions.value ?? -1) || a.adName.localeCompare(b.adName));
}

// --------------------------------------------------------------------------------------------------------------------
// CSV reading (own results and competitor ads). Strict: a wrong cell is reported, never guessed.
// --------------------------------------------------------------------------------------------------------------------

export interface CsvProblem {
  line: number;
  message: string;
}

const norm = (h: string) => h.trim().toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
const EMPTY_WORDS = new Set(["", "n/a", "na", "n_a", "-", "—", "–", "null", "none", "not available", "unavailable", "unknown"]);
const isEmptyCell = (v: string) => EMPTY_WORDS.has(v.trim().toLowerCase());

function headerMap(header: string[], aliases: Record<string, string[]>): Map<string, number> {
  const map = new Map<string, number>();
  const normalized = header.map(norm);
  for (const [field, names] of Object.entries(aliases)) {
    const idx = normalized.findIndex((h) => names.includes(h));
    if (idx >= 0) map.set(field, idx);
  }
  return map;
}

function readInt(raw: string, label: string): { value: number | null; error?: string } {
  if (isEmptyCell(raw)) return { value: null };
  const v = raw.trim().replace(/[,\s_]/g, "");
  if (!/^\d+$/.test(v)) return { value: null, error: `${label} "${raw.trim()}" is not a whole number (write 1200, not 1.2k)` };
  return { value: Number(v) };
}
function readMoney(raw: string, label: string): { value: number | null; error?: string } {
  if (isEmptyCell(raw)) return { value: null };
  const v = raw.trim().replace(/^[$€£]\s*/, "").replace(/[,\s_]/g, "");
  if (!/^\d+(\.\d+)?$/.test(v)) return { value: null, error: `${label} "${raw.trim()}" is not an amount` };
  return { value: Number(v) };
}
function readDate(raw: string, label: string): { value: string | null; error?: string } {
  if (isEmptyCell(raw)) return { value: null };
  const v = raw.trim().slice(0, 10);
  if (!isValidIsoDate(v)) return { value: null, error: `${label} "${raw.trim()}" must be written YYYY-MM-DD (for example 2026-10-19)` };
  return { value: v };
}
function readBool(raw: string, label: string, fallback: boolean): { value: boolean; error?: string } {
  const v = raw.trim().toLowerCase();
  if (v === "") return { value: fallback };
  if (["yes", "y", "true", "1"].includes(v)) return { value: true };
  if (["no", "n", "false", "0"].includes(v)) return { value: false };
  return { value: fallback, error: `${label} "${raw.trim()}" must be yes or no` };
}

const RESULT_ALIASES: Record<string, string[]> = {
  periodStart: ["period_start", "week_start", "week", "start", "start_date", "date", "period"],
  periodEnd: ["period_end", "week_end", "end", "end_date"],
  campaign: ["campaign", "campaign_name"],
  adName: ["ad", "ad_name", "creative", "content"],
  impressions: ["impressions", "impr"],
  clicks: ["clicks", "total_clicks"],
  websiteClicks: ["website_clicks", "landing_page_clicks", "link_clicks", "site_clicks"],
  conversions: ["conversions", "website_conversions", "leads"],
  spend: ["spend", "cost", "total_spent", "amount_spent"],
  currency: ["currency"],
  channel: ["channel", "network", "platform"],
};

function readChannel(raw: string): { value: AdChannel | null; error?: string } {
  const v = raw.trim().toLowerCase().replace(/[^a-z]+/g, " ").trim();
  if (v === "") return { value: null };
  if (v === "linkedin" || v === "linked in") return { value: "linkedin" };
  if (["google", "google ads", "google search", "search", "adwords"].includes(v)) return { value: "google_search" };
  return { value: null, error: `Channel "${raw.trim()}" must be LinkedIn or Google` };
}

/** Our own ad results from a CSV. `periodEnd` defaults to `periodStart` only when the file has no end column at all. */
export function parseAdResultsCsv(input: string, siteId: string, product: AdProductId, defaultChannel: AdChannel = "linkedin"): { rows: AdResultInput[]; errors: CsvProblem[] } {
  const table = parseCsv(input);
  if (table.length < 2) return { rows: [], errors: [{ line: 1, message: "The file needs a header row and at least one data row." }] };
  if (table.length - 1 > ADS_LIMITS.csvMaxRows) return { rows: [], errors: [{ line: 1, message: `That is ${table.length - 1} rows; the limit is ${ADS_LIMITS.csvMaxRows} at a time.` }] };
  const cols = headerMap(table[0]!, RESULT_ALIASES);
  const missing = ["periodStart", "campaign"].filter((f) => !cols.has(f));
  if (missing.length) {
    const names = missing.map((f) => (f === "periodStart" ? "period_start (or week)" : "campaign")).join(" and ");
    return { rows: [], errors: [{ line: 1, message: `The header row has no ${names} column.` }] };
  }
  const rows: AdResultInput[] = [];
  const errors: CsvProblem[] = [];
  const cell = (r: string[], f: string) => (cols.has(f) ? (r[cols.get(f)!] ?? "") : "");
  for (let i = 1; i < table.length; i++) {
    const r = table[i]!;
    const line = i + 1;
    const problems: string[] = [];
    const take = <T extends { error?: string }>(x: T): T => {
      if (x.error) problems.push(x.error);
      return x;
    };
    const start = take(readDate(cell(r, "periodStart"), "Start date"));
    const end = cols.has("periodEnd") ? take(readDate(cell(r, "periodEnd"), "End date")) : start;
    const spend = take(readMoney(cell(r, "spend"), "Spend"));
    const adName = isEmptyCell(cell(r, "adName")) ? null : cell(r, "adName").trim();
    // An ad code names its own product and channel; a row that says otherwise is a mistake, not something to guess around.
    const code = adName ? parseAdCode(adName) : null;
    const said = take(readChannel(cell(r, "channel"))).value;
    if (code && code.product !== product) problems.push(`The ad code ${adName} belongs to ${AD_PRODUCTS[code.product].name}, not ${AD_PRODUCTS[product].name}`);
    if (code && said && said !== code.channel) problems.push(`The ad code ${adName} is a ${AD_CHANNEL_LABEL[code.channel]} ad, but the channel says ${AD_CHANNEL_LABEL[said]}`);
    const draft = {
      siteId,
      product,
      channel: code?.channel ?? said ?? defaultChannel,
      periodStart: start.value ?? "",
      periodEnd: end.value ?? start.value ?? "",
      campaign: cell(r, "campaign"),
      adName,
      impressions: take(readInt(cell(r, "impressions"), "Impressions")).value,
      clicks: take(readInt(cell(r, "clicks"), "Clicks")).value,
      websiteClicks: take(readInt(cell(r, "websiteClicks"), "Website clicks")).value,
      conversions: take(readInt(cell(r, "conversions"), "Conversions")).value,
      spend: spend.value,
      currency: isEmptyCell(cell(r, "currency")) ? null : cell(r, "currency"),
    };
    if (start.value === null && !problems.length) problems.push("The start date is empty");
    if (problems.length) {
      errors.push({ line, message: problems.join("; ") });
      continue;
    }
    const parsed = adResultInputSchema.safeParse(draft);
    if (!parsed.success) errors.push({ line, message: parsed.error.issues.map((x) => x.message).join("; ") });
    else rows.push(parsed.data);
  }
  return { rows, errors: errors.slice(0, ADS_LIMITS.csvMaxErrors) };
}

const AD_ALIASES: Record<string, string[]> = {
  competitor: ["competitor", "company", "advertiser", "watchlist", "name"],
  headline: ["headline", "title"],
  fullText: ["full_text", "text", "ad_text", "body", "copy", "intro_text"],
  cta: ["cta", "call_to_action", "button"],
  format: ["format", "ad_format", "type"],
  adUrl: ["ad_url", "ad_link", "ad_library_url", "url"],
  landingUrl: ["landing_url", "landing_page", "destination_url"],
  firstSeen: ["first_seen", "start_date", "started"],
  lastSeen: ["last_seen", "end_date", "last_active"],
  impressionsLow: ["impressions_low", "impressions_min", "impressions_from"],
  impressionsHigh: ["impressions_high", "impressions_max", "impressions_to"],
  topCountries: ["top_countries", "countries"],
  targetSharePct: ["target_share_pct", "target_market_share", "home_market_share"],
  isMarketing: ["is_marketing", "marketing"],
  imageText: ["image_text", "text_on_image"],
  why: ["why", "why_tracking", "reason"],
  detailsCheckedOn: ["details_checked_on", "checked_on"],
};

const FORMAT_WORDS: Record<string, (typeof AD_FORMATS)[number]> = {
  image: "single_image",
  single_image: "single_image",
  single_image_ad: "single_image",
  video: "video",
  carousel: "carousel",
  document: "document",
  text: "text",
  text_only: "text",
  event: "event",
  message: "message",
  other: "other",
};

export interface CompetitorAdCsvRow {
  competitorName: string;
  input: z.infer<typeof competitorAdRowSchema>;
}

/** Competitor ads from a CSV. The `competitor` column must match a name already on the watchlist (checked by the caller). */
export function parseCompetitorAdsCsv(input: string, siteId: string): { rows: Array<CompetitorAdCsvRow & { line: number }>; errors: CsvProblem[] } {
  const table = parseCsv(input);
  if (table.length < 2) return { rows: [], errors: [{ line: 1, message: "The file needs a header row and at least one data row." }] };
  if (table.length - 1 > ADS_LIMITS.csvMaxRows) return { rows: [], errors: [{ line: 1, message: `That is ${table.length - 1} rows; the limit is ${ADS_LIMITS.csvMaxRows} at a time.` }] };
  const cols = headerMap(table[0]!, AD_ALIASES);
  const missing = ["competitor", "fullText"].filter((f) => !cols.has(f));
  if (missing.length) {
    const names = missing.map((f) => (f === "competitor" ? "competitor" : "full_text")).join(" and ");
    return { rows: [], errors: [{ line: 1, message: `The header row has no ${names} column.` }] };
  }
  const rows: Array<CompetitorAdCsvRow & { line: number }> = [];
  const errors: CsvProblem[] = [];
  const cell = (r: string[], f: string) => (cols.has(f) ? (r[cols.get(f)!] ?? "") : "");
  const orNull = (v: string) => (isEmptyCell(v) ? null : v.trim());
  for (let i = 1; i < table.length; i++) {
    const r = table[i]!;
    const line = i + 1;
    const problems: string[] = [];
    const take = <T extends { error?: string }>(x: T): T => {
      if (x.error) problems.push(x.error);
      return x;
    };
    const fmtRaw = norm(cell(r, "format"));
    if (fmtRaw && !FORMAT_WORDS[fmtRaw]) problems.push(`Format "${cell(r, "format").trim()}" is not one of: ${AD_FORMATS.join(", ")}`);
    const share = (() => {
      const raw = cell(r, "targetSharePct");
      if (isEmptyCell(raw)) return null;
      const n = Number(raw.trim().replace(/%$/, ""));
      if (!Number.isFinite(n) || n < 0 || n > 100) {
        problems.push(`Share in our markets "${raw.trim()}" must be a number from 0 to 100`);
        return null;
      }
      return n;
    })();
    const draft = {
      siteId,
      headline: cell(r, "headline"),
      fullText: cell(r, "fullText"),
      cta: orNull(cell(r, "cta")),
      format: FORMAT_WORDS[fmtRaw] ?? "single_image",
      adUrl: orNull(cell(r, "adUrl")),
      landingUrl: orNull(cell(r, "landingUrl")),
      firstSeen: take(readDate(cell(r, "firstSeen"), "First seen")).value,
      lastSeen: take(readDate(cell(r, "lastSeen"), "Last seen")).value,
      impressionsLow: take(readInt(cell(r, "impressionsLow"), "Impressions (low)")).value,
      impressionsHigh: take(readInt(cell(r, "impressionsHigh"), "Impressions (high)")).value,
      topCountries: cell(r, "topCountries").split(/[;|,\s]+/).map((c) => c.trim()).filter(Boolean),
      targetSharePct: share,
      isMarketing: take(readBool(cell(r, "isMarketing"), "Is marketing", true)).value,
      imageText: cell(r, "imageText"),
      why: cell(r, "why"),
      detailsCheckedOn: take(readDate(cell(r, "detailsCheckedOn"), "Details checked on")).value,
    };
    const competitorName = cell(r, "competitor").trim();
    if (!competitorName) problems.push("The competitor name is empty");
    if (problems.length) {
      errors.push({ line, message: problems.join("; ") });
      continue;
    }
    const parsed = competitorAdRowSchema.safeParse(draft);
    if (!parsed.success) errors.push({ line, message: parsed.error.issues.map((x) => x.message).join("; ") });
    else rows.push({ line, competitorName, input: parsed.data });
  }
  return { rows, errors: errors.slice(0, ADS_LIMITS.csvMaxErrors) };
}

/** Header rows the screens offer as a download, so a file starts in the right shape. */
export const AD_RESULTS_CSV_TEMPLATE =
  "period_start,period_end,channel,campaign,ad_name,impressions,clicks,website_clicks,conversions,spend,currency\n2026-10-12,2026-10-18,LinkedIn,Example campaign,Example ad,12000,240,180,6,350.00,USD\n";
export const COMPETITOR_ADS_CSV_TEMPLATE =
  "competitor,headline,full_text,cta,format,ad_url,landing_url,first_seen,last_seen,impressions_low,impressions_high,top_countries,target_share_pct,is_marketing,image_text,why\nExample Co,Example headline,\"The full ad text, expanded, exactly as shown\",Learn more,single_image,https://www.linkedin.com/ad-library/detail/123,https://example.com/landing,2026-07-01,2026-10-10,50000,100000,US;CA,85,yes,,Why this matters to us\n";

// --------------------------------------------------------------------------------------------------------------------
// API contracts
// --------------------------------------------------------------------------------------------------------------------

export const adsListQuerySchema = z.object({
  siteId: z.string().min(1).max(100),
  product: adProductIdSchema.optional(),
  includeRetired: z.enum(["true", "false"]).default("false"),
  watchlistId: z.string().max(100).optional(),
  category: watchCategorySchema.optional(),
  sort: z.enum(["importance", "recent"]).default("importance"),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(10),
});
export const watchlistResponseSchema = z.object({ items: z.array(watchlistItemSchema) });
export const competitorAdsResponseSchema = z.object({ items: z.array(competitorAdSchema), total: z.number().int(), page: z.number().int(), pageSize: z.number().int() });
export const adResultsResponseSchema = z.object({ items: z.array(adResultSchema), total: z.number().int(), page: z.number().int(), pageSize: z.number().int() });

export const adsImportBodySchema = z.object({ siteId: z.string().min(1).max(100), product: adProductIdSchema, channel: adChannelSchema.default("linkedin"), csv: z.string().min(1).max(ADS_LIMITS.csvMaxChars), fileName: z.string().trim().max(200).default("") });
export const adsImportResponseSchema = z.object({
  imported: z.number().int(),
  duplicates: z.number().int(),
  errors: z.array(z.object({ line: z.number().int(), message: z.string() })),
  batchId: z.string().nullable(),
});
export const adsBatchSchema = z.object({
  id: z.string(),
  kind: z.enum(["results", "competitor_ads"]),
  fileName: z.string(),
  rowCount: z.number().int(),
  liveRows: z.number().int(),
  createdByName: z.string().nullable(),
  createdAt: z.string(),
  retiredAt: z.string().nullable(),
});
export type AdsBatch = z.infer<typeof adsBatchSchema>;
export const adsBatchesResponseSchema = z.object({ items: z.array(adsBatchSchema) });
